import { datadogLogs } from '@datadog/browser-logs';
import { ApolloLink, InMemoryCache } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { HttpLink } from '@apollo/client/link/http';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { map } from 'rxjs';
import { Kind } from 'graphql';
import type { DocumentNode, SelectionNode } from 'graphql';
import { getQueryParameter, USER_UNITS_PREF_KEY } from '../../util';
import {
  DistanceUnitType,
  TempUnitType,
  TestWeightUnitType,
  UserUnitsPrefFragmentFragment,
} from './__generated';
import introspectionQueryResultData from './__introspectionQueryResultData';
import { applyTransformers } from './apply-transformers';
import { VariablesLink } from './localeConversions';
import {
  CelsiusTransformer,
  DateTransformer,
  hasUserSelectedKgUnit,
  InchesToCentimetersTransformer,
  KgPerHectoliterTransformer,
  MassTransformer,
  MetersTransformer,
  MetricTonTransformer,
  Transformers,
} from './transformers';

function hasUserSelectedMeterUnit() {
  const userUnitsPrefVal = localStorage.getItem(USER_UNITS_PREF_KEY);
  const localeUnitSystem: UserUnitsPrefFragmentFragment | null = userUnitsPrefVal
    ? (JSON.parse(userUnitsPrefVal) as UserUnitsPrefFragmentFragment)
    : null;
  const hasUserOptedMeterUnit = localeUnitSystem
    ? localeUnitSystem.distance_unit === DistanceUnitType.Meter
    : false;
  return hasUserOptedMeterUnit;
}

function hasUserSelectedCelsiusUnit() {
  const userUnitsPrefVal = localStorage.getItem(USER_UNITS_PREF_KEY);
  const localeUnitSystem: UserUnitsPrefFragmentFragment | null = userUnitsPrefVal
    ? (JSON.parse(userUnitsPrefVal) as UserUnitsPrefFragmentFragment)
    : null;
  return localeUnitSystem && localeUnitSystem.temp_unit === TempUnitType.Celsius;
}

function hasUserSelectedKgPerHectoliterUnit() {
  const userUnitsPrefVal = localStorage.getItem(USER_UNITS_PREF_KEY);
  const localeUnitSystem: UserUnitsPrefFragmentFragment | null = userUnitsPrefVal
    ? (JSON.parse(userUnitsPrefVal) as UserUnitsPrefFragmentFragment)
    : null;
  return (
    localeUnitSystem &&
    localeUnitSystem.test_weight_unit === TestWeightUnitType.KilogramPerHectoliter
  );
}

const transformers: Transformers = {
  Account: {
    updated_at: DateTransformer,
  },
  AerationWindow: {
    start: DateTransformer,
    end: DateTransformer,
  },
  BargeCoverPelletLink: {
    start_epoch: DateTransformer,
    end_epoch: DateTransformer,
  },
  BinEvent: {
    epoch_time: DateTransformer,
  },
  FanController: {
    updated_at: DateTransformer,
  },
  FanControllerResetRequest: {
    request_epoch: DateTransformer,
  },
  FanControllerPingRequest: {
    request_epoch: DateTransformer,
  },
  FanControllerSetConfigRequest: {
    request_epoch: DateTransformer,
  },
  FanControllerResetResponse: {
    response_epoch: DateTransformer,
  },
  FanControllerHeartbeatResponse: {
    request_epoch: DateTransformer,
    response_epoch: DateTransformer,
  },
  FanControllerGetConfigResponse: {
    response_epoch: DateTransformer,
  },
  FanControllerDebugResponse: {
    response_epoch: DateTransformer,
  },
  FanControllerRequest: {
    request_epoch: DateTransformer,
    updated_at: DateTransformer,
  },
  FanControllerRunWindow: {
    start_epoch: DateTransformer,
    end_epoch: DateTransformer,
  },
  FanControllerState: {
    config_epoch: DateTransformer,
    response_epoch: DateTransformer,
  },
  FanControllerStatus: {
    updated_at: DateTransformer,
  },
  ...(hasUserSelectedCelsiusUnit() && {
    ForecastDayV2: {
      temp_high_f: CelsiusTransformer,
      temp_low_f: CelsiusTransformer,
    },
  }),
  GrainBinFanController: {
    start_date: DateTransformer,
    end_date: DateTransformer,
    updated_at: DateTransformer,
  },
  GrainBinFanControllerStatus: {
    updated_at: DateTransformer,
  },
  GrainBinFanSettings: {
    updated_at: DateTransformer,
  },
  GrainBinTelemetry: {
    as_of: DateTransformer,
  },
  GrainBinStoragePeriod: {
    start_epoch: DateTransformer,
    end_epoch: DateTransformer,
    created_at: DateTransformer,
  },
  GrainBinStoragePeriodWithOngoingStorageFlag: {
    start_epoch: DateTransformer,
    end_epoch: DateTransformer,
    created_at: DateTransformer,
  },
  GrainEnvPoint: {
    epoch_time: DateTransformer,
    ...(hasUserSelectedCelsiusUnit() && { temp_f: CelsiusTransformer }),
  },
  GrainEnvPointV2: {
    epoch_time: DateTransformer,
    ...(hasUserSelectedCelsiusUnit() && { temp_f: CelsiusTransformer }),
  },
  WeatherHistoryPoint: {
    epoch_time: DateTransformer,
    ...(hasUserSelectedCelsiusUnit() && { temp_f: CelsiusTransformer }),
  },
  GrainStatus: {
    epoch_time: DateTransformer,
    ...(hasUserSelectedCelsiusUnit() && { temp_f: CelsiusTransformer }),
  },
  GrainStatusV2: {
    epoch_time: DateTransformer,
    ...(hasUserSelectedCelsiusUnit() && {
      temp_f: CelsiusTransformer,
    }),
  },
  GrainTelemetry: {
    as_of: DateTransformer,
  },
  GrainTelemetryHistory: {
    start_epoch: DateTransformer,
    end_epoch: DateTransformer,
  },
  GrainBin: {
    fan_guidance_end_date: DateTransformer,
    fan_guidance_start_date: DateTransformer,
    ...(hasUserSelectedMeterUnit() && {
      height_ft: MetersTransformer,
      diameter_ft: MetersTransformer,
    }),
    ...(hasUserSelectedCelsiusUnit() && {
      current_grain_temp: CelsiusTransformer,
    }),
  },
  ...(hasUserSelectedMeterUnit() && {
    GrainBinFanModelOutput: {
      diameter: InchesToCentimetersTransformer,
    },
    FanModelResultVal: {
      diameter: InchesToCentimetersTransformer,
    },
  }),
  GrainBinTicket: {
    created_at: DateTransformer,
    epoch_time: DateTransformer,
    updated_at: DateTransformer,
    ...(hasUserSelectedKgUnit() && {
      weight_in_lbs: MassTransformer,
    }),
    ...(hasUserSelectedKgPerHectoliterUnit() && {
      test_weight_in_lbs: KgPerHectoliterTransformer,
    }),
  },
  SensorGeneratedGrainTicket: {
    created_at: DateTransformer,
    epoch_time: DateTransformer,
    updated_at: DateTransformer,
    ...(hasUserSelectedKgUnit() && {
      weight_in_lbs: MassTransformer,
    }),
    ...(hasUserSelectedKgPerHectoliterUnit() && {
      test_weight_in_lbs: KgPerHectoliterTransformer,
    }),
  },
  ...(hasUserSelectedCelsiusUnit() && {
    GrainContainerAerationRun: {
      avg_temp_f: CelsiusTransformer,
      start_temp_f: CelsiusTransformer,
      end_temp_f: CelsiusTransformer,
      current_grain_temp: CelsiusTransformer,
    },
  }),
  ...(hasUserSelectedCelsiusUnit() && {
    GrainTemporaryInputConditions: {
      current_grain_temp: CelsiusTransformer,
    },
  }),
  ...(hasUserSelectedKgUnit() && {
    GrainBinLevelSample: {
      bushel: MetricTonTransformer,
    },
    GrainBinLevelHistory: { max_bushels: MetricTonTransformer },
    GrainBinTicketLevelSample: { bushel: MetricTonTransformer },
  }),
  HubContainerLink: {
    start_epoch: DateTransformer,
    end_epoch: DateTransformer,
    ...(hasUserSelectedMeterUnit() && {
      hub_offset_ft: MetersTransformer,
    }),
  },
  HubEventHistoryEdge: {
    start: DateTransformer,
    end: DateTransformer,
  },
  HubEventsEdge: {
    as_of: DateTransformer,
  },
  HubGpsEvent: {
    epoch_time: DateTransformer,
    read_time: DateTransformer,
    updated_at: DateTransformer,
  },
  HubHeadspaceEvent: {
    epoch_time: DateTransformer,
  },
  HubLocation: {
    epoch_time: DateTransformer,
    read_time: DateTransformer,
    updated_at: DateTransformer,
  },
  HubPelletEvent: {
    epoch_time: DateTransformer,
  },
  HubSignalEvent: {
    epoch_time: DateTransformer,
  },
  HubStatusEvent: {
    epoch_time: DateTransformer,
    updated_at: DateTransformer,
  },
  HubWeather: {
    epoch_time: DateTransformer,
  },
  HubWeatherForecastHourly: {
    epoch_time: DateTransformer,
  },
  ParticleDevice: {
    last_heard: DateTransformer,
  },
  Pellet: {
    created_at: DateTransformer,
  },
  PelletGroup: {
    created_at: DateTransformer,
  },
  PelletGroupPelletLink: {
    reassigned_at: DateTransformer,
    created_at: DateTransformer,
  },
  PelletImage: {
    created_at: DateTransformer,
    updated_at: DateTransformer,
  },
  PelletRef: {
    created_at: DateTransformer,
  },
  WeatherData: {
    epoch_time: DateTransformer,
  },
  RunWindow: {
    start_epoch: DateTransformer,
    end_epoch: DateTransformer,
  },
  CO2AlertInfo: {
    co2_alert_sent_on: DateTransformer,
  },
  ...(hasUserSelectedCelsiusUnit() && {
    TempMinMax: {
      temp_low_f: CelsiusTransformer,
      temp_high_f: CelsiusTransformer,
    },
  }),
};

export const createLinks = (host: string) => {
  // Transformer link: intercepts every GraphQL response and applies unit
  // conversions (temperature, distance, mass) based on the user's locale
  // settings. Uses rxjs pipe(map(...)) per Apollo Client 4's recommended
  // pattern for synchronous response transformation in custom links.
  const transformerLink = new ApolloLink((operation, forward) => {
    return forward(operation).pipe(
      map((response) => {
        if ('data' in response && response.data) {
          // oxlint-disable-next-line typescript/no-explicit-any -- intentionally retained
          response.data = applyTransformers(response.data, transformers) as Record<string, any>;
        }
        return response;
      })
    );
  });

  // Auth link: injects the Bearer token into every request's Authorization header.
  // Supports customer care impersonation (customer_care_token takes precedence)
  // and URL-based token auth (?token=...) for email deep links.
  // When no token is present, returns { headers } to preserve existing headers
  // without adding authorization — required by AC4's SetContextLink contract.
  const auth_link = new SetContextLink((prevContext) => {
    const hasQueryParamsToken = window.location.href.includes('?token=');
    const customerCareToken = localStorage.getItem('customer_care_token');
    const userToken = customerCareToken ? customerCareToken : localStorage.getItem('token');
    const token = hasQueryParamsToken ? getQueryParameter('token') : userToken;
    const headers = prevContext.headers || {};
    return token
      ? {
          headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : '',
          },
        }
      : { headers };
  });

  let result = transformerLink.concat(auth_link);

  // Error link: logs GraphQL and network errors to the console for debugging.
  // Uses AC4's CombinedGraphQLErrors.is() and ServerError.is() type guards
  // instead of AC2's instanceof checks. ServerError.bodyText is parsed as JSON
  // to extract structured error details from the server response.
  const error_link = new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`
        );
        datadogLogs.logger.error(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`
        );
      });
    }
    if (ServerError.is(error)) {
      let parsedResult: { name?: string } | undefined;
      try {
        parsedResult = JSON.parse(error.bodyText) as { name?: string };
      } catch {
        // bodyText is not valid JSON
      }
      if (parsedResult && parsedResult.name) {
        console.error(`[Network server error]: ${parsedResult.name}`);
        datadogLogs.logger.error(`[Network server error]: ${parsedResult.name}`);
      }
      console.error(`[Network error]: ${error}`);
      datadogLogs.logger.error(`[Network error]: ${error}`);
    }
    // Store the last error on window for external error reporting tools (e.g. Datadog RUM)
    // and for test assertions. Structured as a legacy-compatible shape with graphQLErrors
    // and networkError fields so existing error handlers continue to work.
    (window as { last_error?: unknown }).last_error = {
      graphQLErrors: CombinedGraphQLErrors.is(error) ? error.errors : undefined,
      networkError: ServerError.is(error) ? error : undefined,
      operation,
    };
  });
  result = result.concat(error_link);

  // variables link to manipulate operation variables
  const variables_link = new VariablesLink();
  result = result.concat(variables_link);

  // Strip __typename from ALL operation variables (queries, mutations, subscriptions).
  // Placed after variables_link (which does locale conversions) and before HTTP transport.
  const removeTypenameLink = new RemoveTypenameFromVariablesLink();
  result = result.concat(removeTypenameLink);

  // link to use if batching (default)
  const batchHttpLink = new BatchHttpLink({
    uri: `${host}/graphql`,
    batchMax: 20,
    // 50ms batch window (default is 10ms). Apollo pollInterval polls fire via
    // independent setTimeout calls that drift apart over time. A wider window
    // catches more concurrent polls in the same HTTP request, matching the
    // batching behavior of the pre-migration AC2 client.
    batchInterval: 50,
  });
  // link to use if not batching
  const httpLink = new HttpLink({ uri: `${host}/graphql` });

  // Triple-split: @defer → HttpLink (multipart), important → HttpLink (debatch), default → BatchHttpLink
  // BatchHttpLink does NOT support multipart/mixed responses required by @defer.
  // Operations containing @defer must route through the standard HttpLink.
  //
  // NOTE (June 2026): @defer is NOT actively used. No component sends @defer queries.
  // The infrastructure is in place for future use when Lambda Function URLs enable
  // HTTP streaming. Currently Lambda + API Gateway buffers the entire multipart response,
  // negating the incremental delivery benefit. See steering/graphql-codegen.md "@defer
  // Directive — Deferred" section for the full rationale.
  const http_link = ApolloLink.split(
    (operation) => hasDefer(operation),
    httpLink, // @defer needs multipart support — only HttpLink handles multipart/mixed
    ApolloLink.split(
      (operation) => operation.getContext().important === true,
      httpLink, // important: debatch for lower latency
      batchHttpLink // default: batch for reduced HTTP overhead
    )
  );

  return result.concat(http_link);
};

// Detect @defer in a query's AST. Used by the link chain split to route
// @defer queries to HttpLink instead of BatchHttpLink (since BatchHttpLink
// doesn't support multipart/mixed responses required for incremental delivery).
export function hasDefer(operation: { query: DocumentNode }): boolean {
  const { query } = operation;
  return query.definitions.some(
    (def) =>
      def.kind === Kind.OPERATION_DEFINITION && def.selectionSet.selections.some(selectionHasDefer)
  );
}

// Recursively checks a selection node (and its children) for the @defer directive.
// Handles all three SelectionNode kinds per the GraphQL AST spec:
// - InlineFragment: may have @defer and may have nested selections
// - Field: may have nested selections (subfields could contain deferred inline fragments)
// - FragmentSpread: may have @defer but has no inline selectionSet (resolved at runtime)
function selectionHasDefer(selection: SelectionNode): boolean {
  // Check if THIS node has @defer — applies to all three SelectionNode kinds uniformly
  if (selection.directives?.some((d) => d.name.value === 'defer')) return true;
  // Recurse into child selections (only InlineFragment and Field have selectionSet)
  if (
    (selection.kind === Kind.INLINE_FRAGMENT || selection.kind === Kind.FIELD) &&
    selection.selectionSet
  ) {
    return selection.selectionSet.selections.some(selectionHasDefer);
  }
  // FragmentSpread without @defer — no further recursion possible (the spread's
  // referenced fragment is resolved elsewhere; we only check inline directives here)
  return false;
}

export const createCache = () =>
  new InMemoryCache({
    possibleTypes: introspectionQueryResultData.possibleTypes,
    typePolicies: {
      // --- Normalizable types: keyFields replaces the legacy dataIdFromObject switch ---
      Account: { keyFields: ['account_id'] },
      Viewer: { keyFields: ['user_id'] },
      Barge: { keyFields: ['barge_id'] },
      BargeAccountLink: { keyFields: ['barge_id'] },
      BargeCoverPelletLink: { keyFields: ['barge_cover_pellet_link_id'] },
      FanController: { keyFields: ['fan_controller_id_next'] },
      // Request/response types use composite keys (fan_controller_id + epoch).
      // The epoch fields are Date objects (transformed by the link chain) which
      // serialize to ISO strings in the cache key automatically.
      FanControllerPingRequest: { keyFields: ['fan_controller_id', 'request_epoch'] },
      FanControllerResetRequest: { keyFields: ['fan_controller_id', 'request_epoch'] },
      FanControllerSetConfigRequest: { keyFields: ['fan_controller_id', 'request_epoch'] },
      FanControllerResetResponse: { keyFields: ['fan_controller_id', 'response_epoch'] },
      FanControllerHeartbeatResponse: { keyFields: ['fan_controller_id', 'response_epoch'] },
      FanControllerGetConfigResponse: { keyFields: ['fan_controller_id', 'response_epoch'] },
      FanControllerDebugResponse: { keyFields: ['fan_controller_id', 'response_epoch'] },
      FanControllerState: { keyFields: ['fan_controller_id'] },
      FanControllerState2: { keyFields: ['core_id'] },
      GrainBin: { keyFields: ['grain_bin_id'] },
      GrainBinFanSettings: { keyFields: ['grain_bin_id'] },
      GrainBinAccountLink: { keyFields: ['grain_bin_id'] },
      GrainContainer: {
        keyFields: ['container_id', 'container_type'],
        fields: {
          aeration_run_history: { merge: true },
          aeration_schedule: { merge: false },
          telemetry: { merge: true },
          telemetry_history_v2: { merge: true },
        },
      },
      GrainBinStoragePeriod: { keyFields: ['grain_bin_storage_cycle_id'] },
      GrainBinStoragePeriodWithOngoingStorageFlag: { keyFields: ['grain_bin_storage_cycle_id'] },
      GrainBinFanController: { keyFields: ['grain_bin_fan_controller_id'] },
      Hub: { keyFields: ['hub_id'] },
      HubContainerLink: { keyFields: ['hub_container_link_id'] },
      PelletCable: { keyFields: ['pellet_group_id'] },
      PelletGroup: { keyFields: ['pellet_group_id'] },
      PelletGroupPelletLink: { keyFields: ['pellet_group_pellet_link_id'] },
      Pile: { keyFields: ['pile_id'] },
      PileAccountLink: { keyFields: ['pile_id'] },
      PileCableLink: { keyFields: ['pile_cable_link_id'] },
      User: { keyFields: ['user_id'] },

      // --- Query field-level merge policies ---
      Query: {
        fields: {
          // containers_weather is fetched in batches via fetchMore; the hook's
          // updateQuery callback handles merging, so the cache should accept
          // the incoming array as-is to avoid the "Cache data may be lost" warning.
          containers_weather: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },

      // --- Non-normalizable types: merge: true prevents cache replacement warnings ---
      GrainTelemetry: { merge: true },
      GrainTelemetryHistoryV2: { merge: true },
    },
  });
